import { useState, useMemo, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { PlatformLayout } from "./platform-layout";
import { format, parseISO, startOfMonth } from "date-fns";
import {
  DollarSign, TrendingUp, TrendingDown, AlertTriangle, CheckCircle2,
  Plus, Pencil, Trash2, Upload, Download, Calculator, Users, ChevronDown,
  ChevronRight, X, Search, Filter, Loader2, FileText, Building2,
  CreditCard, Zap, Megaphone, Briefcase, Rocket, MoreHorizontal,
  ArrowUpRight, ArrowDownRight, Clock, Calendar,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { VoiceToTextTextarea } from "@/components/voice-field";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

// ─── Types ────────────────────────────────────────────────────────────────────

interface PnlData {
  periods: string[];
  grossRevenueCents: number;
  stripeFeesFromRevenueCents: number;
  netRevenueCents: number;
  totalExpensesCents: number;
  byCategory: Record<string, number>;
  grossMarginCents: number;
  grossMarginPct: number;
  openArCents: number;
  upcomingApCents: number;
}

interface CashFlowData {
  expectedInflowCents: number;
  expectedOutflowCents: number;
  netForecastCents: number;
  openInvoiceCount: number;
  windows: Record<"d30"|"d60"|"d90", { outflowCents: number; inflowCents: number; netCents: number }>;
  upcomingExpenses: { id: string; description: string; category: string; amountCents: number; dueDate: string }[];
}

interface Expense {
  id: string;
  description: string;
  category: string;
  vendorKey: string | null;
  amountCents: number;
  currency: string;
  billingDate: string;
  dueDate: string | null;
  paidAt: string | null;
  isRecurring: boolean;
  recurringPeriod: string | null;
  notes: string | null;
  uploadId: string | null;
}

interface Employee {
  id: string;
  name: string;
  role: string | null;
  employmentType: string;
  payRateCents: number;
  payFrequency: string;
  startDate: string | null;
  endDate: string | null;
  notes: string | null;
}

interface CommissionRep {
  id: string;
  name: string;
  email: string;
  activeRule: { id: string; ratePercent: string; effectiveFrom: string } | null;
  totalEarnedCents: number;
  unpaidCents: number;
  payouts: CommissionPayout[];
}

interface CommissionPayout {
  id: string;
  period: string;
  grossCollectedCents: number;
  ratePercent: string;
  commissionCents: number;
  paidAt: string | null;
}

interface ArRow {
  customerId: string;
  companyName: string;
  mrrCents: number;
  rep: string | null;
  totalBilledCents: number;
  totalPaidCents: number;
  totalStripeFees: number;
  netCollectedCents: number;
  outstandingCents: number;
  ltvCents: number;
  invoiceCount: number;
  lastPaidAt: string | null;
  aged: Record<string, number>;
}

interface BillingUpload {
  id: string;
  fileName: string;
  vendorKey: string | null;
  vendorLabel: string | null;
  period: string | null;
  totalCents: number | null;
  currency: string;
  status: string;
  notes: string | null;
  createdAt: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const CATEGORIES = [
  { value: "api", label: "API Costs", icon: Zap },
  { value: "hosting", label: "Hosting", icon: Building2 },
  { value: "employee", label: "Employee", icon: Users },
  { value: "contractor", label: "Contractor", icon: Briefcase },
  { value: "ad_spend", label: "Ad Spend", icon: Megaphone },
  { value: "startup", label: "Startup Cost", icon: Rocket },
  { value: "other", label: "Other", icon: MoreHorizontal },
];

const VENDORS = [
  { value: "openai", label: "OpenAI" },
  { value: "anthropic", label: "Anthropic" },
  { value: "twilio", label: "Twilio" },
  { value: "elevenlabs", label: "ElevenLabs" },
  { value: "ipinfo", label: "IPInfo" },
  { value: "replit", label: "Replit" },
  { value: "stripe", label: "Stripe" },
  { value: "google", label: "Google" },
  { value: "meta", label: "Meta" },
  { value: "tiktok", label: "TikTok" },
  { value: "youtube", label: "YouTube" },
  { value: "other", label: "Other" },
];

const CAT_COLORS: Record<string, string> = {
  api: "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400",
  hosting: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  employee: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  contractor: "bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400",
  ad_spend: "bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400",
  startup: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  other: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtMoney(cents: number, currency = "USD", compact = false) {
  if (compact && Math.abs(cents) >= 100000) {
    return new Intl.NumberFormat("en-US", { style: "currency", currency, notation: "compact", maximumFractionDigits: 1 }).format(cents / 100);
  }
  return new Intl.NumberFormat("en-US", { style: "currency", currency, minimumFractionDigits: 2 }).format(cents / 100);
}

function fmtDate(iso: string | null) {
  if (!iso) return "—";
  return format(parseISO(iso), "MMM d, yyyy");
}

function nowPeriod() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function nowQuarter() {
  return Math.ceil((new Date().getMonth() + 1) / 3);
}

// ─── Stat card ────────────────────────────────────────────────────────────────

function StatCard({ label, value, sub, trend, color = "default", icon: Icon }: {
  label: string; value: string; sub?: string; trend?: "up" | "down" | null;
  color?: "default" | "green" | "red" | "amber" | "blue";
  icon?: React.ElementType;
}) {
  const colorMap = {
    default: "bg-white dark:bg-gray-900",
    green: "bg-green-50 dark:bg-green-900/20",
    red: "bg-red-50 dark:bg-red-900/20",
    amber: "bg-amber-50 dark:bg-amber-900/20",
    blue: "bg-blue-50 dark:bg-blue-900/20",
  };
  return (
    <div className={cn("rounded-lg border p-4", colorMap[color])}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-1">{label}</div>
          <div className="text-xl font-bold tabular-nums text-gray-900 dark:text-gray-100">{value}</div>
          {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
        </div>
        {Icon && (
          <div className="shrink-0 h-8 w-8 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
            <Icon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
          </div>
        )}
      </div>
      {trend && (
        <div className={cn("flex items-center gap-1 mt-2 text-xs font-medium", trend === "up" ? "text-green-600" : "text-red-600")}>
          {trend === "up" ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
        </div>
      )}
    </div>
  );
}

// ─── Category badge ───────────────────────────────────────────────────────────

function CatBadge({ cat }: { cat: string }) {
  const cfg = CATEGORIES.find((c) => c.value === cat);
  const label = cfg?.label ?? cat;
  return (
    <span className={cn("inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium", CAT_COLORS[cat] ?? CAT_COLORS.other)}>
      {label}
    </span>
  );
}

// ─── Expense form dialog ──────────────────────────────────────────────────────

function ExpenseDialog({
  open, onClose, initial,
}: { open: boolean; onClose: () => void; initial?: Expense }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const isEdit = !!initial;

  const [form, setForm] = useState({
    description: initial?.description ?? "",
    category: initial?.category ?? "other",
    vendorKey: initial?.vendorKey ?? "",
    amountCents: initial ? String(initial.amountCents / 100) : "",
    billingDate: initial?.billingDate ? initial.billingDate.split("T")[0] : new Date().toISOString().split("T")[0],
    dueDate: initial?.dueDate ? initial.dueDate.split("T")[0] : "",
    paidAt: initial?.paidAt ? initial.paidAt.split("T")[0] : "",
    isRecurring: initial?.isRecurring ?? false,
    recurringPeriod: initial?.recurringPeriod ?? "",
    notes: initial?.notes ?? "",
  });

  const mut = useMutation({
    mutationFn: (body: any) =>
      isEdit
        ? apiRequest("PATCH", `/api/platform/billing/expenses/${initial!.id}`, body)
        : apiRequest("POST", "/api/platform/billing/expenses", body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/platform/billing/expenses"] });
      qc.invalidateQueries({ queryKey: ["/api/platform/billing/pnl"] });
      toast({ title: isEdit ? "Expense updated" : "Expense added" });
      onClose();
    },
    onError: () => toast({ title: "Error", description: "Save failed", variant: "destructive" }),
  });

  const handleSubmit = () => {
    const amountCents = Math.round(parseFloat(form.amountCents) * 100);
    if (!form.description || isNaN(amountCents)) {
      toast({ title: "Description and amount are required", variant: "destructive" });
      return;
    }
    mut.mutate({
      description: form.description,
      category: form.category,
      vendorKey: form.vendorKey || null,
      amountCents,
      billingDate: form.billingDate,
      dueDate: form.dueDate || null,
      paidAt: form.paidAt || null,
      isRecurring: form.isRecurring,
      recurringPeriod: form.recurringPeriod || null,
      notes: form.notes || null,
    });
  };

  const f = (k: string) => (v: any) => setForm((p) => ({ ...p, [k]: v }));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Expense" : "Add Expense"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div className="space-y-1">
            <Label>Description *</Label>
            <Input value={form.description} onChange={(e) => f("description")(e.target.value)} placeholder="e.g. Replit Team Plan — May 2026" data-testid="input-expense-desc" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Category</Label>
              <Select value={form.category} onValueChange={f("category")}>
                <SelectTrigger data-testid="select-expense-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Vendor</Label>
              <Select value={form.vendorKey || "_none"} onValueChange={(v) => f("vendorKey")(v === "_none" ? "" : v)}>
                <SelectTrigger data-testid="select-expense-vendor"><SelectValue placeholder="Select vendor" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">— None —</SelectItem>
                  {VENDORS.map((v) => <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Amount (USD) *</Label>
              <Input value={form.amountCents} onChange={(e) => f("amountCents")(e.target.value)} placeholder="0.00" type="number" step="0.01" data-testid="input-expense-amount" />
            </div>
            <div className="space-y-1">
              <Label>Billing Date *</Label>
              <Input type="date" value={form.billingDate} onChange={(e) => f("billingDate")(e.target.value)} data-testid="input-expense-billing-date" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Due Date</Label>
              <Input type="date" value={form.dueDate} onChange={(e) => f("dueDate")(e.target.value)} data-testid="input-expense-due-date" />
            </div>
            <div className="space-y-1">
              <Label>Paid On</Label>
              <Input type="date" value={form.paidAt} onChange={(e) => f("paidAt")(e.target.value)} data-testid="input-expense-paid-at" />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Checkbox
              id="recurring"
              checked={form.isRecurring}
              onCheckedChange={(v) => f("isRecurring")(!!v)}
              data-testid="checkbox-expense-recurring"
            />
            <Label htmlFor="recurring" className="cursor-pointer">Recurring</Label>
            {form.isRecurring && (
              <Select value={form.recurringPeriod || "monthly"} onValueChange={f("recurringPeriod")}>
                <SelectTrigger className="h-8 w-32"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="annual">Annual</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>
          <div className="space-y-1">
            <Label>Notes</Label>
            <VoiceToTextTextarea value={form.notes} onChange={(e) => f("notes")(e.target.value)} rows={2} placeholder="Optional notes" data-testid="textarea-expense-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={mut.isPending} data-testid="button-expense-save">
            {mut.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
            {isEdit ? "Save Changes" : "Add Expense"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Employee form dialog ─────────────────────────────────────────────────────

function EmployeeDialog({ open, onClose, initial }: { open: boolean; onClose: () => void; initial?: Employee }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const isEdit = !!initial;
  const [form, setForm] = useState({
    name: initial?.name ?? "",
    role: initial?.role ?? "",
    employmentType: initial?.employmentType ?? "employee",
    payRateCents: initial ? String(initial.payRateCents / 100) : "",
    payFrequency: initial?.payFrequency ?? "monthly",
    startDate: initial?.startDate ? initial.startDate.split("T")[0] : "",
    endDate: initial?.endDate ? initial.endDate.split("T")[0] : "",
    notes: initial?.notes ?? "",
  });
  const f = (k: string) => (v: any) => setForm((p) => ({ ...p, [k]: v }));
  const mut = useMutation({
    mutationFn: (body: any) =>
      isEdit
        ? apiRequest("PATCH", `/api/platform/billing/employees/${initial!.id}`, body)
        : apiRequest("POST", "/api/platform/billing/employees", body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/platform/billing/employees"] });
      toast({ title: isEdit ? "Updated" : "Employee added" });
      onClose();
    },
    onError: () => toast({ title: "Error", variant: "destructive" }),
  });

  const submit = () => {
    const payRateCents = Math.round(parseFloat(form.payRateCents) * 100);
    if (!form.name) { toast({ title: "Name is required", variant: "destructive" }); return; }
    mut.mutate({
      name: form.name,
      role: form.role || null,
      employmentType: form.employmentType,
      payRateCents: isNaN(payRateCents) ? 0 : payRateCents,
      payFrequency: form.payFrequency,
      startDate: form.startDate || null,
      endDate: form.endDate || null,
      notes: form.notes || null,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{isEdit ? "Edit Person" : "Add Employee / Contractor"}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-1">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Name *</Label>
              <Input value={form.name} onChange={(e) => f("name")(e.target.value)} data-testid="input-employee-name" />
            </div>
            <div className="space-y-1">
              <Label>Role / Title</Label>
              <Input value={form.role} onChange={(e) => f("role")(e.target.value)} placeholder="e.g. Founder" data-testid="input-employee-role" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Type</Label>
              <Select value={form.employmentType} onValueChange={f("employmentType")}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="employee">Employee</SelectItem>
                  <SelectItem value="contractor">Contractor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Pay Frequency</Label>
              <Select value={form.payFrequency} onValueChange={f("payFrequency")}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="hourly">Hourly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="annual">Annual</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1">
            <Label>Pay Rate (USD)</Label>
            <Input type="number" step="0.01" value={form.payRateCents} onChange={(e) => f("payRateCents")(e.target.value)} placeholder="0.00" data-testid="input-employee-pay" />
            <p className="text-xs text-muted-foreground">
              {form.payFrequency === "hourly" ? "per hour" : form.payFrequency === "annual" ? "per year" : "per month"}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Start Date</Label>
              <Input type="date" value={form.startDate} onChange={(e) => f("startDate")(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>End Date</Label>
              <Input type="date" value={form.endDate} onChange={(e) => f("endDate")(e.target.value)} />
            </div>
          </div>
          <div className="space-y-1">
            <Label>Notes</Label>
            <VoiceToTextTextarea value={form.notes} onChange={(e) => f("notes")(e.target.value)} rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={mut.isPending} data-testid="button-employee-save">
            {mut.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
            {isEdit ? "Save" : "Add"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Upload dialog ────────────────────────────────────────────────────────────

function UploadDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [meta, setMeta] = useState({
    vendorKey: "",
    vendorLabel: "",
    period: nowPeriod(),
    totalCents: "",
    notes: "",
  });
  const [uploading, setUploading] = useState(false);

  const handleFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) setFiles(Array.from(e.target.files));
  };

  const handleSubmit = async () => {
    if (!files.length) { toast({ title: "Choose at least one file", variant: "destructive" }); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      if (meta.vendorKey) fd.append("vendorKey", meta.vendorKey);
      if (meta.vendorLabel) fd.append("vendorLabel", meta.vendorLabel);
      if (meta.period) fd.append("period", meta.period);
      if (meta.totalCents) fd.append("totalCents", String(Math.round(parseFloat(meta.totalCents) * 100)));
      if (meta.notes) fd.append("notes", meta.notes);

      const res = await fetch("/api/platform/billing/uploads", {
        method: "POST",
        body: fd,
        credentials: "include",
      });
      if (!res.ok) throw new Error(await res.text());
      qc.invalidateQueries({ queryKey: ["/api/platform/billing/uploads"] });
      toast({ title: `${files.length} statement${files.length > 1 ? "s" : ""} uploaded` });
      onClose();
    } catch (err: any) {
      toast({ title: "Upload failed", description: err.message, variant: "destructive" });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Upload Billing Statements</DialogTitle></DialogHeader>
        <div className="space-y-3 py-1">
          <div
            className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-gray-400 transition-colors"
            onClick={() => fileRef.current?.click()}
            data-testid="dropzone-billing-upload"
          >
            <Upload className="h-8 w-8 mx-auto text-gray-300 mb-2" />
            {files.length > 0 ? (
              <div className="space-y-1">
                {files.map((f) => (
                  <p key={f.name} className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate">{f.name}</p>
                ))}
                <p className="text-xs text-muted-foreground mt-1">{files.length} file{files.length > 1 ? "s" : ""} selected</p>
              </div>
            ) : (
              <div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Click to select files</p>
                <p className="text-xs text-muted-foreground mt-1">PDF, PNG, JPG — multiple allowed</p>
              </div>
            )}
            <input ref={fileRef} type="file" multiple accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden" onChange={handleFiles} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Vendor</Label>
              <Select value={meta.vendorKey || "_none"} onValueChange={(v) => setMeta((p) => ({ ...p, vendorKey: v === "_none" ? "" : v }))}>
                <SelectTrigger><SelectValue placeholder="Select vendor" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">— None —</SelectItem>
                  {VENDORS.map((v) => <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Billing Period</Label>
              <Input type="month" value={meta.period} onChange={(e) => setMeta((p) => ({ ...p, period: e.target.value }))} />
            </div>
          </div>
          <div className="space-y-1">
            <Label>Total Amount on Statement (USD)</Label>
            <Input type="number" step="0.01" value={meta.totalCents} onChange={(e) => setMeta((p) => ({ ...p, totalCents: e.target.value }))} placeholder="0.00" data-testid="input-upload-total" />
          </div>
          <div className="space-y-1">
            <Label>Notes</Label>
            <VoiceToTextTextarea value={meta.notes} onChange={(e) => setMeta((p) => ({ ...p, notes: e.target.value }))} rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={uploading} data-testid="button-upload-submit">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Upload className="h-4 w-4 mr-1.5" />}
            Upload {files.length > 1 ? `${files.length} Files` : "File"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Commission rule dialog ───────────────────────────────────────────────────

function CommissionRuleDialog({ open, onClose, rep }: { open: boolean; onClose: () => void; rep: CommissionRep }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [rate, setRate] = useState(rep.activeRule ? String(Number(rep.activeRule.ratePercent) * 100) : "10");
  const [effectiveFrom, setEffectiveFrom] = useState(new Date().toISOString().split("T")[0]);
  const [notes, setNotes] = useState("");

  const mut = useMutation({
    mutationFn: (body: any) => apiRequest("POST", "/api/platform/billing/commissions/rules", body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/platform/billing/commissions/reps"] });
      toast({ title: "Commission rule set" });
      onClose();
    },
    onError: () => toast({ title: "Error", variant: "destructive" }),
  });

  const submit = () => {
    const rateDecimal = parseFloat(rate) / 100;
    if (isNaN(rateDecimal) || rateDecimal < 0 || rateDecimal > 1) {
      toast({ title: "Rate must be 0–100%", variant: "destructive" }); return;
    }
    mut.mutate({ platformUserId: rep.id, ratePercent: rateDecimal, effectiveFrom, notes: notes || null });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Set Commission Rate — {rep.name}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-1">
          <div className="space-y-1">
            <Label>Commission Rate (%)</Label>
            <Input type="number" min="0" max="100" step="0.5" value={rate} onChange={(e) => setRate(e.target.value)} data-testid="input-commission-rate" />
          </div>
          <div className="space-y-1">
            <Label>Effective From</Label>
            <Input type="date" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Notes</Label>
            <VoiceToTextTextarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={mut.isPending} data-testid="button-commission-rule-save">
            {mut.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
            Save Rate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB: P&L Dashboard
// ═══════════════════════════════════════════════════════════════════════════════

function PnlTab() {
  const [viewMode, setViewMode] = useState<"month" | "quarter">("month");
  const [period, setPeriod] = useState(nowPeriod());
  const [quarter, setQuarter] = useState(nowQuarter());
  const [year, setYear] = useState(new Date().getFullYear());

  const qp = viewMode === "month"
    ? `period=${period}`
    : `quarter=${quarter}&year=${year}`;

  const { data, isLoading } = useQuery<PnlData>({
    queryKey: [`/api/platform/billing/pnl?${qp}`],
  });

  const CURRENT_YEAR = new Date().getFullYear();

  return (
    <div className="space-y-5">
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3">
        <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as any)}>
          <TabsList className="h-8">
            <TabsTrigger value="month" className="text-xs px-3">Monthly</TabsTrigger>
            <TabsTrigger value="quarter" className="text-xs px-3">Quarterly</TabsTrigger>
          </TabsList>
        </Tabs>
        {viewMode === "month" ? (
          <Input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className="h-8 w-44 text-sm" data-testid="input-pnl-period" />
        ) : (
          <div className="flex items-center gap-2">
            <Select value={String(quarter)} onValueChange={(v) => setQuarter(Number(v))}>
              <SelectTrigger className="h-8 w-24"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Q1</SelectItem>
                <SelectItem value="2">Q2</SelectItem>
                <SelectItem value="3">Q3</SelectItem>
                <SelectItem value="4">Q4</SelectItem>
              </SelectContent>
            </Select>
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="h-8 w-28"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[CURRENT_YEAR - 2, CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1].map((y) => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16 gap-2 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" /> Loading P&L…
        </div>
      ) : data ? (
        <>
          {/* Top-line KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Gross Revenue" value={fmtMoney(data.grossRevenueCents)} sub="Before Stripe fees" icon={TrendingUp} color="green" />
            <StatCard label="Net Revenue" value={fmtMoney(data.netRevenueCents)} sub={`Stripe fees: ${fmtMoney(data.stripeFeesFromRevenueCents)}`} icon={DollarSign} color="blue" />
            <StatCard label="Total Expenses" value={fmtMoney(data.totalExpensesCents)} icon={TrendingDown} color={data.totalExpensesCents > 0 ? "red" : "default"} />
            <StatCard
              label="Gross Margin"
              value={`${data.grossMarginPct >= 0 ? "+" : ""}${data.grossMarginPct}%`}
              sub={fmtMoney(data.grossMarginCents)}
              color={data.grossMarginCents >= 0 ? "green" : "red"}
            />
          </div>

          {/* Secondary KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            <StatCard label="Open AR (unpaid invoices)" value={fmtMoney(data.openArCents)} icon={Clock} color={data.openArCents > 0 ? "amber" : "default"} />
            <StatCard label="Upcoming AP (next 30 days)" value={fmtMoney(data.upcomingApCents)} icon={Calendar} color={data.upcomingApCents > 0 ? "amber" : "default"} />
            <StatCard label="Net Position" value={fmtMoney(data.netRevenueCents - data.totalExpensesCents)} color={data.netRevenueCents - data.totalExpensesCents >= 0 ? "green" : "red"} />
          </div>

          {/* Expense breakdown */}
          {data.totalExpensesCents > 0 && (
            <div className="rounded-lg border bg-white dark:bg-gray-900 p-4">
              <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Expenses by Category</div>
              <div className="space-y-2">
                {Object.entries(data.byCategory)
                  .sort((a, b) => b[1] - a[1])
                  .map(([cat, cents]) => {
                    const pct = Math.round((cents / data.totalExpensesCents) * 100);
                    return (
                      <div key={cat} className="flex items-center gap-3">
                        <div className="w-24 shrink-0"><CatBadge cat={cat} /></div>
                        <div className="flex-1 h-2 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                          <div className="h-full bg-blue-500 rounded-full" style={{ width: `${pct}%` }} />
                        </div>
                        <div className="w-20 text-right text-sm tabular-nums text-gray-700 dark:text-gray-300">{fmtMoney(cents)}</div>
                        <div className="w-10 text-right text-xs text-muted-foreground">{pct}%</div>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}

          {data.totalExpensesCents === 0 && data.grossRevenueCents === 0 && (
            <div className="rounded-lg border border-dashed py-10 text-center text-muted-foreground">
              <DollarSign className="h-10 w-10 mx-auto opacity-20 mb-3" />
              <p className="text-sm">No financial data for this period yet.</p>
              <p className="text-xs mt-1">Add expenses in the AP Ledger tab and connect Stripe to see revenue.</p>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB: AP Ledger
// ═══════════════════════════════════════════════════════════════════════════════

function ApLedgerTab() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [unpaidOnly, setUnpaidOnly] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState<Expense | null>(null);
  const [showUpload, setShowUpload] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const params = new URLSearchParams();
  if (catFilter !== "all") params.set("category", catFilter);
  if (unpaidOnly) params.set("unpaid", "1");
  const qs = params.toString();

  const { data, isLoading } = useQuery<{ expenses: Expense[] }>({
    queryKey: [`/api/platform/billing/expenses${qs ? `?${qs}` : ""}`],
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/platform/billing/expenses/${id}`, {}),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["/api/platform/billing/expenses"] }); toast({ title: "Deleted" }); },
  });

  const bulkPaidMut = useMutation({
    mutationFn: (ids: string[]) => apiRequest("POST", "/api/platform/billing/expenses/bulk-paid", { ids }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/platform/billing/expenses"] });
      setSelected(new Set());
      toast({ title: `Marked paid` });
    },
  });

  const expenses = useMemo(() => {
    const rows = data?.expenses ?? [];
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter((e) =>
      e.description.toLowerCase().includes(q) ||
      (e.vendorKey ?? "").toLowerCase().includes(q) ||
      e.category.toLowerCase().includes(q)
    );
  }, [data, search]);

  const totalShown = expenses.reduce((s, e) => s + e.amountCents, 0);
  const paidShown = expenses.filter((e) => e.paidAt).reduce((s, e) => s + e.amountCents, 0);
  const unpaidShown = totalShown - paidShown;

  const toggleSelect = (id: string) => setSelected((p) => {
    const n = new Set(p);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const exportCsv = () => window.open("/api/platform/billing/expenses/export", "_blank");

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={() => setShowAdd(true)} data-testid="button-add-expense">
          <Plus className="h-4 w-4 mr-1.5" /> Add Expense
        </Button>
        <Button size="sm" variant="outline" onClick={() => setShowUpload(true)} data-testid="button-upload-statement">
          <Upload className="h-4 w-4 mr-1.5" /> Upload Statement
        </Button>
        {selected.size > 0 && (
          <Button size="sm" variant="outline" onClick={() => bulkPaidMut.mutate(Array.from(selected))} disabled={bulkPaidMut.isPending} data-testid="button-bulk-paid">
            <CheckCircle2 className="h-4 w-4 mr-1.5" /> Mark {selected.size} Paid
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={exportCsv} className="ml-auto" data-testid="button-export-expenses">
          <Download className="h-4 w-4 mr-1.5" /> Export CSV
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…" className="pl-8 h-8 w-52 text-sm" data-testid="input-expense-search" />
        </div>
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger className="h-8 w-40"><SelectValue placeholder="All categories" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <Checkbox checked={unpaidOnly} onCheckedChange={(v) => setUnpaidOnly(!!v)} />
          Unpaid only
        </label>
        <div className="ml-auto flex items-center gap-4 text-xs text-muted-foreground">
          <span>Total: <strong className="text-gray-900 dark:text-gray-100">{fmtMoney(totalShown)}</strong></span>
          <span>Paid: <strong className="text-green-600">{fmtMoney(paidShown)}</strong></span>
          <span>Unpaid: <strong className="text-amber-600">{fmtMoney(unpaidShown)}</strong></span>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-lg border bg-white dark:bg-gray-900 overflow-hidden">
        <div className="grid grid-cols-[28px_1fr_120px_100px_100px_90px_100px] gap-3 px-4 py-2 bg-gray-50 dark:bg-gray-800/60 border-b text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          <div />
          <div>Description</div>
          <div>Category</div>
          <div className="text-right">Amount</div>
          <div>Billing Date</div>
          <div>Status</div>
          <div className="text-right">Actions</div>
        </div>

        {isLoading && (
          <div className="flex items-center justify-center py-12 gap-2 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" /> Loading…
          </div>
        )}
        {!isLoading && expenses.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <FileText className="h-8 w-8 opacity-30 mb-2" />
            <p className="text-sm">No expenses yet. Add your first one above.</p>
          </div>
        )}
        {expenses.map((e) => (
          <div
            key={e.id}
            className={cn(
              "grid grid-cols-[28px_1fr_120px_100px_100px_90px_100px] gap-3 px-4 py-2.5 border-b hover:bg-gray-50/50 dark:hover:bg-gray-800/30 text-sm items-center",
              selected.has(e.id) && "bg-blue-50/40 dark:bg-blue-900/10"
            )}
            data-testid={`expense-row-${e.id}`}
          >
            <Checkbox checked={selected.has(e.id)} onCheckedChange={() => toggleSelect(e.id)} />
            <div>
              <div className="font-medium text-gray-900 dark:text-gray-100 truncate">{e.description}</div>
              {e.vendorKey && <div className="text-xs text-muted-foreground">{VENDORS.find(v => v.value === e.vendorKey)?.label ?? e.vendorKey}</div>}
              {e.isRecurring && <span className="text-[10px] text-blue-500 font-medium uppercase">↻ {e.recurringPeriod}</span>}
            </div>
            <div><CatBadge cat={e.category} /></div>
            <div className="text-right font-medium tabular-nums">{fmtMoney(e.amountCents)}</div>
            <div className="text-muted-foreground text-xs">{fmtDate(e.billingDate)}</div>
            <div>
              {e.paidAt ? (
                <span className="inline-flex items-center gap-1 text-xs text-green-700 dark:text-green-400">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Paid
                </span>
              ) : e.dueDate && new Date(e.dueDate) < new Date() ? (
                <span className="inline-flex items-center gap-1 text-xs text-red-600 dark:text-red-400">
                  <AlertTriangle className="h-3.5 w-3.5" /> Overdue
                </span>
              ) : (
                <span className="text-xs text-amber-600 dark:text-amber-400">Unpaid</span>
              )}
            </div>
            <div className="flex items-center gap-1 justify-end">
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setEditing(e)} data-testid={`button-edit-expense-${e.id}`}>
                <Pencil className="h-3.5 w-3.5" />
              </Button>
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 hover:text-red-700" onClick={() => deleteMut.mutate(e.id)} data-testid={`button-delete-expense-${e.id}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      {showAdd && <ExpenseDialog open onClose={() => setShowAdd(false)} />}
      {editing && <ExpenseDialog open onClose={() => setEditing(null)} initial={editing} />}
      {showUpload && <UploadDialog open onClose={() => setShowUpload(false)} />}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB: Accounts Receivable
// ═══════════════════════════════════════════════════════════════════════════════

function ArTab() {
  const [search, setSearch] = useState("");
  const { data, isLoading } = useQuery<{ ar: ArRow[] }>({ queryKey: ["/api/platform/billing/ar"] });

  const rows = useMemo(() => {
    const all = data?.ar ?? [];
    if (!search.trim()) return all;
    const q = search.toLowerCase();
    return all.filter((r) => r.companyName.toLowerCase().includes(q) || (r.rep ?? "").toLowerCase().includes(q));
  }, [data, search]);

  const totalOutstanding = rows.reduce((s, r) => s + r.outstandingCents, 0);
  const totalLtv = rows.reduce((s, r) => s + r.ltvCents, 0);
  const totalFees = rows.reduce((s, r) => s + r.totalStripeFees, 0);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search customer or rep…" className="pl-8 h-8 w-56 text-sm" data-testid="input-ar-search" />
        </div>
        <div className="ml-auto flex items-center gap-4 text-xs text-muted-foreground">
          <span>Outstanding: <strong className="text-amber-600">{fmtMoney(totalOutstanding)}</strong></span>
          <span>Total LTV: <strong className="text-green-600">{fmtMoney(totalLtv)}</strong></span>
          <span>Stripe fees: <strong className="text-gray-700 dark:text-gray-300">{fmtMoney(totalFees)}</strong></span>
        </div>
      </div>

      <div className="rounded-lg border bg-white dark:bg-gray-900 overflow-hidden">
        <div className="grid grid-cols-[1fr_90px_90px_90px_90px_100px_140px] gap-3 px-4 py-2 bg-gray-50 dark:bg-gray-800/60 border-b text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          <div>Customer</div>
          <div className="text-right">MRR</div>
          <div className="text-right">Billed</div>
          <div className="text-right">Net Collected</div>
          <div className="text-right">Outstanding</div>
          <div className="text-right">LTV</div>
          <div>Aged Buckets</div>
        </div>

        {isLoading && (
          <div className="flex items-center justify-center py-12 gap-2 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" /> Loading…
          </div>
        )}

        {rows.map((r) => {
          const hasAged = Object.values(r.aged).some((v) => v > 0);
          return (
            <div key={r.customerId} className="grid grid-cols-[1fr_90px_90px_90px_90px_100px_140px] gap-3 px-4 py-3 border-b hover:bg-gray-50/50 dark:hover:bg-gray-800/30 text-sm items-center" data-testid={`ar-row-${r.customerId}`}>
              <div>
                <div className="font-medium text-gray-900 dark:text-gray-100">{r.companyName}</div>
                {r.rep && <div className="text-xs text-muted-foreground">Rep: {r.rep}</div>}
                <div className="text-xs text-muted-foreground">{r.invoiceCount} invoice{r.invoiceCount !== 1 ? "s" : ""}</div>
              </div>
              <div className="text-right tabular-nums text-sm">{fmtMoney(r.mrrCents)}</div>
              <div className="text-right tabular-nums text-sm">{fmtMoney(r.totalBilledCents)}</div>
              <div className="text-right tabular-nums text-sm text-green-700 dark:text-green-400 font-medium">{fmtMoney(r.netCollectedCents)}</div>
              <div className={cn("text-right tabular-nums text-sm font-medium", r.outstandingCents > 0 ? "text-amber-600" : "text-muted-foreground")}>
                {r.outstandingCents > 0 ? fmtMoney(r.outstandingCents) : "—"}
              </div>
              <div className="text-right tabular-nums text-sm font-semibold">{fmtMoney(r.ltvCents)}</div>
              <div className="text-xs space-y-0.5">
                {hasAged ? (
                  <>
                    {r.aged["0_30"] > 0 && <div className="text-blue-600">0–30d: {fmtMoney(r.aged["0_30"], "USD", true)}</div>}
                    {r.aged["31_60"] > 0 && <div className="text-amber-600">31–60d: {fmtMoney(r.aged["31_60"], "USD", true)}</div>}
                    {r.aged["61_90"] > 0 && <div className="text-orange-600">61–90d: {fmtMoney(r.aged["61_90"], "USD", true)}</div>}
                    {r.aged["over_90"] > 0 && <div className="text-red-600 font-semibold">90+d: {fmtMoney(r.aged["over_90"], "USD", true)}</div>}
                  </>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </div>
            </div>
          );
        })}

        {!isLoading && rows.length === 0 && (
          <div className="py-12 text-center text-muted-foreground text-sm">
            No AR data. Link customers to Stripe to see receivables.
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB: Commissions
// ═══════════════════════════════════════════════════════════════════════════════

function CommissionsTab() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [calcPeriod, setCalcPeriod] = useState(nowPeriod());
  const [ruleTarget, setRuleTarget] = useState<CommissionRep | null>(null);

  const { data, isLoading } = useQuery<{ reps: CommissionRep[] }>({
    queryKey: ["/api/platform/billing/commissions/reps"],
  });

  const calcMut = useMutation({
    mutationFn: () => apiRequest("POST", "/api/platform/billing/commissions/calculate", { period: calcPeriod }),
    onSuccess: (d: any) => {
      qc.invalidateQueries({ queryKey: ["/api/platform/billing/commissions/reps"] });
      toast({ title: `Commission calculated for ${d.payouts?.length ?? 0} rep(s)` });
    },
    onError: () => toast({ title: "Calculation failed", variant: "destructive" }),
  });

  const paidMut = useMutation({
    mutationFn: (id: string) => apiRequest("POST", `/api/platform/billing/commissions/payouts/${id}/paid`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/platform/billing/commissions/reps"] });
      toast({ title: "Payout marked paid" });
    },
  });

  const reps = data?.reps ?? [];
  const totalUnpaid = reps.reduce((s, r) => s + r.unpaidCents, 0);

  return (
    <div className="space-y-5">
      {/* Calculate bar */}
      <div className="flex items-center gap-3 p-4 rounded-lg border bg-gray-50 dark:bg-gray-800/40">
        <Calculator className="h-5 w-5 text-gray-400 shrink-0" />
        <div className="flex-1">
          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">Calculate Commissions</div>
          <div className="text-xs text-muted-foreground">Computes from collected Stripe invoices for the period</div>
        </div>
        <Input type="month" value={calcPeriod} onChange={(e) => setCalcPeriod(e.target.value)} className="h-8 w-40 text-sm" data-testid="input-commission-period" />
        <Button size="sm" onClick={() => calcMut.mutate()} disabled={calcMut.isPending} data-testid="button-calculate-commissions">
          {calcMut.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Calculator className="h-4 w-4 mr-1.5" />}
          Calculate
        </Button>
      </div>

      {totalUnpaid > 0 && (
        <div className="flex items-center gap-3 px-4 py-2.5 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700 text-sm">
          <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />
          <span className="text-amber-800 dark:text-amber-300"><strong>{fmtMoney(totalUnpaid)}</strong> in unpaid commissions across {reps.filter(r => r.unpaidCents > 0).length} rep(s)</span>
        </div>
      )}

      {isLoading && <div className="flex items-center justify-center py-12 gap-2 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>}

      <div className="space-y-4">
        {reps.map((rep) => (
          <div key={rep.id} className="rounded-lg border bg-white dark:bg-gray-900" data-testid={`commission-rep-${rep.id}`}>
            <div className="flex items-center gap-4 px-4 py-3 border-b">
              <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-900 dark:text-gray-100">{rep.name}</div>
                <div className="text-xs text-muted-foreground">{rep.email}</div>
              </div>
              <div className="text-right">
                <div className="text-xs text-muted-foreground">Rate</div>
                <div className="text-sm font-semibold">
                  {rep.activeRule ? `${(Number(rep.activeRule.ratePercent) * 100).toFixed(1)}%` : <span className="text-muted-foreground">—</span>}
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs text-muted-foreground">Total Earned</div>
                <div className="text-sm font-semibold text-green-600">{fmtMoney(rep.totalEarnedCents)}</div>
              </div>
              <div className="text-right">
                <div className="text-xs text-muted-foreground">Unpaid</div>
                <div className={cn("text-sm font-semibold", rep.unpaidCents > 0 ? "text-amber-600" : "text-muted-foreground")}>
                  {rep.unpaidCents > 0 ? fmtMoney(rep.unpaidCents) : "—"}
                </div>
              </div>
              <Button size="sm" variant="outline" onClick={() => setRuleTarget(rep)} data-testid={`button-set-rate-${rep.id}`}>
                Set Rate
              </Button>
            </div>

            {/* Payout history */}
            {rep.payouts.length > 0 && (
              <div className="px-4 py-2 space-y-1">
                {rep.payouts.map((p) => (
                  <div key={p.id} className="flex items-center gap-3 text-sm py-1">
                    <span className="text-muted-foreground w-16">{p.period}</span>
                    <span className="flex-1 text-xs text-muted-foreground">Gross: {fmtMoney(p.grossCollectedCents)} × {(Number(p.ratePercent) * 100).toFixed(1)}%</span>
                    <span className="font-medium">{fmtMoney(p.commissionCents)}</span>
                    {p.paidAt ? (
                      <span className="text-xs text-green-600 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Paid</span>
                    ) : (
                      <Button size="sm" variant="outline" className="h-6 text-xs px-2" onClick={() => paidMut.mutate(p.id)} disabled={paidMut.isPending} data-testid={`button-mark-payout-paid-${p.id}`}>
                        Mark Paid
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {rep.payouts.length === 0 && (
              <div className="px-4 py-3 text-xs text-muted-foreground">No payouts calculated yet — run Calculate above for a period.</div>
            )}
          </div>
        ))}

        {!isLoading && reps.length === 0 && (
          <div className="py-10 text-center text-muted-foreground text-sm">
            No platform users found. Add users to the platform to track commissions.
          </div>
        )}
      </div>

      {ruleTarget && <CommissionRuleDialog open onClose={() => setRuleTarget(null)} rep={ruleTarget} />}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB: Cash Flow Forecast
// ═══════════════════════════════════════════════════════════════════════════════

function CashFlowTab() {
  const { data, isLoading } = useQuery<CashFlowData>({ queryKey: ["/api/platform/billing/cashflow"] });

  return (
    <div className="space-y-5">
      {isLoading && <div className="flex items-center justify-center py-12 gap-2 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>}
      {data && (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatCard label="Expected Inflow (open AR)" value={fmtMoney(data.expectedInflowCents)} sub={`${data.openInvoiceCount} open invoice${data.openInvoiceCount !== 1 ? "s" : ""}`} color="green" icon={ArrowUpRight} />
            <StatCard label="Expected Outflow (AP due ≤90d)" value={fmtMoney(data.expectedOutflowCents)} color={data.expectedOutflowCents > 0 ? "red" : "default"} icon={ArrowDownRight} />
            <StatCard label="90-Day Net Forecast" value={fmtMoney(data.netForecastCents)} color={data.netForecastCents >= 0 ? "green" : "red"} />
          </div>

          {/* 30/60/90 windows */}
          <div className="rounded-lg border bg-white dark:bg-gray-900 overflow-hidden">
            <div className="px-4 py-2.5 border-b bg-gray-50 dark:bg-gray-800/60 text-sm font-semibold text-gray-900 dark:text-gray-100">Projection Windows</div>
            <div className="divide-y">
              {(["d30", "d60", "d90"] as const).map((w, i) => {
                const label = ["Next 30 days", "Next 60 days", "Next 90 days"][i];
                const wd = data.windows[w];
                return (
                  <div key={w} className="grid grid-cols-4 gap-4 px-4 py-3 text-sm">
                    <div className="font-medium text-gray-900 dark:text-gray-100">{label}</div>
                    <div className="text-right">
                      <div className="text-xs text-muted-foreground">Inflow</div>
                      <div className="font-medium text-green-600 tabular-nums">{fmtMoney(wd.inflowCents)}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-muted-foreground">Outflow</div>
                      <div className="font-medium text-red-600 tabular-nums">{fmtMoney(wd.outflowCents)}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-muted-foreground">Net</div>
                      <div className={cn("font-bold tabular-nums", wd.netCents >= 0 ? "text-green-600" : "text-red-600")}>{fmtMoney(wd.netCents)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Upcoming AP */}
          {data.upcomingExpenses.length > 0 && (
            <div className="rounded-lg border bg-white dark:bg-gray-900 overflow-hidden">
              <div className="px-4 py-2.5 border-b bg-gray-50 dark:bg-gray-800/60 text-sm font-semibold text-gray-900 dark:text-gray-100">
                Upcoming Bills (next 90 days)
              </div>
              <div className="divide-y">
                {data.upcomingExpenses.map((e) => (
                  <div key={e.id} className="flex items-center gap-4 px-4 py-2.5 text-sm">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 dark:text-gray-100 truncate">{e.description}</div>
                      <div><CatBadge cat={e.category} /></div>
                    </div>
                    <div className="text-right">
                      <div className="font-medium tabular-nums">{fmtMoney(e.amountCents)}</div>
                      <div className="text-xs text-muted-foreground">Due {fmtDate(e.dueDate)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {data.upcomingExpenses.length === 0 && data.expectedInflowCents === 0 && (
            <div className="py-10 text-center text-muted-foreground text-sm">
              No cash flow data yet. Add expenses with due dates and connect Stripe to see forecasts.
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB: Employees & Statements
// ═══════════════════════════════════════════════════════════════════════════════

function PeopleAndStatementsTab() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [showAddEmp, setShowAddEmp] = useState(false);
  const [editEmp, setEditEmp] = useState<Employee | null>(null);
  const [showUpload, setShowUpload] = useState(false);

  const { data: empData, isLoading: empLoading } = useQuery<{ employees: Employee[] }>({ queryKey: ["/api/platform/billing/employees"] });
  const { data: uplData } = useQuery<{ uploads: BillingUpload[] }>({ queryKey: ["/api/platform/billing/uploads"] });

  const deleteEmpMut = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/platform/billing/employees/${id}`, {}),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["/api/platform/billing/employees"] }); toast({ title: "Removed" }); },
  });

  const deleteUploadMut = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/platform/billing/uploads/${id}`, {}),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["/api/platform/billing/uploads"] }); toast({ title: "Deleted" }); },
  });

  const employees = empData?.employees ?? [];
  const uploads = uplData?.uploads ?? [];

  const annualPayroll = employees.reduce((s, e) => {
    if (e.payFrequency === "annual") return s + e.payRateCents;
    if (e.payFrequency === "monthly") return s + e.payRateCents * 12;
    return s;
  }, 0);

  return (
    <div className="space-y-6">
      {/* Employees section */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">People</h3>
            <p className="text-xs text-muted-foreground">Employees, contractors, and founders</p>
          </div>
          <Button size="sm" onClick={() => setShowAddEmp(true)} data-testid="button-add-employee">
            <Plus className="h-4 w-4 mr-1.5" /> Add Person
          </Button>
        </div>

        {annualPayroll > 0 && (
          <div className="mb-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-800/40 border text-sm flex items-center gap-3">
            <Users className="h-4 w-4 text-gray-400" />
            <span className="text-muted-foreground">Annual payroll equivalent:</span>
            <span className="font-semibold text-gray-900 dark:text-gray-100">{fmtMoney(annualPayroll)}</span>
            <span className="text-muted-foreground">·</span>
            <span className="text-muted-foreground">{fmtMoney(Math.round(annualPayroll / 12))}/mo</span>
          </div>
        )}

        <div className="rounded-lg border bg-white dark:bg-gray-900 overflow-hidden">
          {empLoading && <div className="py-8 text-center text-muted-foreground text-sm">Loading…</div>}
          {!empLoading && employees.length === 0 && (
            <div className="py-10 text-center text-muted-foreground">
              <Users className="h-8 w-8 mx-auto opacity-20 mb-2" />
              <p className="text-sm">No people added yet. Add yourself as a founder to start tracking compensation.</p>
            </div>
          )}
          {employees.map((e) => (
            <div key={e.id} className="flex items-center gap-4 px-4 py-3 border-b last:border-b-0 text-sm" data-testid={`employee-row-${e.id}`}>
              <div className="h-8 w-8 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center shrink-0 text-xs font-semibold text-gray-600 dark:text-gray-300">
                {(e.name[0] ?? "?").toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-900 dark:text-gray-100">{e.name}</div>
                <div className="text-xs text-muted-foreground">{e.role ?? "—"} · {e.employmentType === "contractor" ? "Contractor" : "Employee"}</div>
              </div>
              <div className="text-right">
                <div className="font-medium tabular-nums">{fmtMoney(e.payRateCents)}</div>
                <div className="text-xs text-muted-foreground">/{e.payFrequency}</div>
              </div>
              <div className="text-xs text-muted-foreground">
                {e.startDate ? `Since ${fmtDate(e.startDate)}` : ""}
                {e.endDate ? ` → ${fmtDate(e.endDate)}` : ""}
              </div>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setEditEmp(e)} data-testid={`button-edit-employee-${e.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => deleteEmpMut.mutate(e.id)} data-testid={`button-delete-employee-${e.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Statements section */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Billing Statements</h3>
            <p className="text-xs text-muted-foreground">Uploaded vendor invoices & receipts</p>
          </div>
          <Button size="sm" variant="outline" onClick={() => setShowUpload(true)} data-testid="button-upload-statement-2">
            <Upload className="h-4 w-4 mr-1.5" /> Upload Statements
          </Button>
        </div>

        <div className="rounded-lg border bg-white dark:bg-gray-900 overflow-hidden">
          {uploads.length === 0 && (
            <div className="py-10 text-center text-muted-foreground">
              <FileText className="h-8 w-8 mx-auto opacity-20 mb-2" />
              <p className="text-sm">No statements uploaded yet.</p>
              <p className="text-xs mt-1">Upload Replit invoices, Twilio bills, etc. — multiple files at once supported.</p>
            </div>
          )}
          {uploads.map((u) => (
            <div key={u.id} className="flex items-center gap-4 px-4 py-3 border-b last:border-b-0 text-sm" data-testid={`upload-row-${u.id}`}>
              <FileText className="h-5 w-5 text-gray-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-900 dark:text-gray-100 truncate">{u.fileName}</div>
                <div className="text-xs text-muted-foreground">
                  {u.vendorLabel ?? (u.vendorKey ? VENDORS.find(v => v.value === u.vendorKey)?.label : null) ?? "Unknown vendor"}
                  {u.period && ` · ${u.period}`}
                  {" · "}{fmtDate(u.createdAt)}
                </div>
              </div>
              {u.totalCents != null && (
                <div className="text-right">
                  <div className="font-medium tabular-nums">{fmtMoney(u.totalCents)}</div>
                  <div className="text-xs text-muted-foreground">statement total</div>
                </div>
              )}
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => deleteUploadMut.mutate(u.id)} data-testid={`button-delete-upload-${u.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          ))}
        </div>
      </div>

      {showAddEmp && <EmployeeDialog open onClose={() => setShowAddEmp(false)} />}
      {editEmp && <EmployeeDialog open onClose={() => setEditEmp(null)} initial={editEmp} />}
      {showUpload && <UploadDialog open onClose={() => setShowUpload(false)} />}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Credit Margin (V2 cost-vs-charge audit — "don't lose money")
// ═══════════════════════════════════════════════════════════════════════════════

interface MarginRow {
  pool: string;
  operation: string;
  events: number;
  ourCostUsd: number;
  revenueUsd: number;
  marginUsd: number;
  marginPct: number | null;
  unitsProduced: number;
  avgCostPerUnitUsd: number | null;
  losing: boolean;
}
interface MarginReport {
  rows: MarginRow[];
  totals: Omit<MarginRow, "pool" | "operation">;
}

function fmtUsd(v: number | null | undefined, dp = 2) {
  if (v == null || isNaN(v)) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: dp,
    maximumFractionDigits: dp,
  }).format(v);
}
function fmtPct(v: number | null | undefined) {
  if (v == null || isNaN(v)) return "—";
  return `${(v * 100).toFixed(1)}%`;
}

function CreditMarginTab() {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const qp = useMemo(() => {
    const p = new URLSearchParams();
    if (from) p.set("from", new Date(from).toISOString());
    if (to) {
      const end = new Date(to);
      end.setHours(23, 59, 59, 999);
      p.set("to", end.toISOString());
    }
    return p.toString();
  }, [from, to]);
  const { data, isLoading } = useQuery<MarginReport>({
    queryKey: [`/api/platform/billing/credit-margin${qp ? `?${qp}` : ""}`],
  });

  const losingCount = data?.rows.filter((r) => r.losing).length ?? 0;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <Label className="text-xs text-muted-foreground">From</Label>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-8 w-40 text-sm" data-testid="input-margin-from" />
        </div>
        <div className="flex flex-col gap-1">
          <Label className="text-xs text-muted-foreground">To</Label>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-8 w-40 text-sm" data-testid="input-margin-to" />
        </div>
        {(from || to) && (
          <Button variant="ghost" size="sm" className="h-8" onClick={() => { setFrom(""); setTo(""); }} data-testid="button-margin-clear">
            <X className="h-3.5 w-3.5 mr-1" /> Clear
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading margin report…
        </div>
      ) : !data || data.rows.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground text-sm" data-testid="text-margin-empty">
          No billable activity recorded for this range yet.
        </div>
      ) : (
        <>
          {losingCount > 0 && (
            <div className="flex items-center gap-2 rounded-md border border-red-300 bg-red-50 dark:bg-red-950/30 dark:border-red-900 px-3 py-2 text-sm text-red-700 dark:text-red-300" data-testid="alert-margin-losing">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              {losingCount} operation{losingCount !== 1 ? "s are" : " is"} at or below cost — review pricing.
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard label="Our True Cost" value={fmtUsd(data.totals.ourCostUsd)} sub={`${data.totals.events} billable events`} icon={TrendingDown} color={data.totals.ourCostUsd > 0 ? "amber" : "default"} />
            <StatCard label="Charged (Revenue)" value={fmtUsd(data.totals.revenueUsd)} icon={DollarSign} color="blue" />
            <StatCard label="Realized Margin" value={fmtUsd(data.totals.marginUsd)} sub={fmtPct(data.totals.marginPct)} icon={TrendingUp} color={data.totals.marginUsd >= 0 ? "green" : "red"} />
            <StatCard label="Avg Cost / Lead" value={fmtUsd(data.totals.avgCostPerUnitUsd, 4)} sub={`${data.totals.unitsProduced} leads produced`} icon={CheckCircle2} color="default" />
          </div>

          <div className="rounded-lg border border-gray-200 dark:border-gray-800 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900/50 text-xs text-muted-foreground">
                <tr>
                  <th className="text-left font-medium px-3 py-2">Pool</th>
                  <th className="text-left font-medium px-3 py-2">Operation</th>
                  <th className="text-right font-medium px-3 py-2">Events</th>
                  <th className="text-right font-medium px-3 py-2">Our Cost</th>
                  <th className="text-right font-medium px-3 py-2">Charged</th>
                  <th className="text-right font-medium px-3 py-2">Margin</th>
                  <th className="text-right font-medium px-3 py-2">Margin %</th>
                  <th className="text-right font-medium px-3 py-2">Avg Cost/Lead</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={`${r.pool}-${r.operation}`} className={cn("border-t border-gray-100 dark:border-gray-800", r.losing && "bg-red-50/60 dark:bg-red-950/20")} data-testid={`row-margin-${r.pool}-${r.operation}`}>
                    <td className="px-3 py-2"><Badge variant="outline" className="text-[10px] uppercase">{r.pool}</Badge></td>
                    <td className="px-3 py-2 font-medium flex items-center gap-1.5">
                      {r.losing && <AlertTriangle className="h-3.5 w-3.5 text-red-500 shrink-0" />}
                      {r.operation}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.events}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtUsd(r.ourCostUsd)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtUsd(r.revenueUsd)}</td>
                    <td className={cn("px-3 py-2 text-right tabular-nums font-medium", r.marginUsd >= 0 ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400")}>{fmtUsd(r.marginUsd)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtPct(r.marginPct)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtUsd(r.avgCostPerUnitUsd, 4)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Main page
// ═══════════════════════════════════════════════════════════════════════════════

export default function PlatformBilling() {
  return (
    <PlatformLayout>
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <DollarSign className="h-6 w-6 text-gray-400" />
          Billing
        </h1>
        <p className="text-sm text-muted-foreground mt-0.5">
          Comprehensive accounts payable, receivable, commissions, and cash flow for the platform.
        </p>
      </div>

      <Tabs defaultValue="pnl" className="space-y-5">
        <TabsList className="h-9">
          <TabsTrigger value="pnl" className="text-xs px-4" data-testid="tab-billing-pnl">P&amp;L Dashboard</TabsTrigger>
          <TabsTrigger value="ap" className="text-xs px-4" data-testid="tab-billing-ap">AP Ledger</TabsTrigger>
          <TabsTrigger value="ar" className="text-xs px-4" data-testid="tab-billing-ar">Accounts Receivable</TabsTrigger>
          <TabsTrigger value="commissions" className="text-xs px-4" data-testid="tab-billing-commissions">Commissions</TabsTrigger>
          <TabsTrigger value="cashflow" className="text-xs px-4" data-testid="tab-billing-cashflow">Cash Flow</TabsTrigger>
          <TabsTrigger value="margin" className="text-xs px-4" data-testid="tab-billing-margin">Credit Margin</TabsTrigger>
          <TabsTrigger value="people" className="text-xs px-4" data-testid="tab-billing-people">People &amp; Statements</TabsTrigger>
        </TabsList>

        <TabsContent value="pnl"><PnlTab /></TabsContent>
        <TabsContent value="ap"><ApLedgerTab /></TabsContent>
        <TabsContent value="ar"><ArTab /></TabsContent>
        <TabsContent value="commissions"><CommissionsTab /></TabsContent>
        <TabsContent value="cashflow"><CashFlowTab /></TabsContent>
        <TabsContent value="margin"><CreditMarginTab /></TabsContent>
        <TabsContent value="people"><PeopleAndStatementsTab /></TabsContent>
      </Tabs>
    </div>
    </PlatformLayout>
  );
}
